/**
 * PunchPhotoTile — Wave 5.9 Build 11 (2026-02).
 *
 * Shared card for a single punch item photo. Purpose:
 *   - Small corner chip for the label (Before/After/Progress/custom) —
 *     no more full-width bar overlaying the photo's contributor line.
 *   - Caption row BELOW the photo: `uploaded_by_name · timestamp`.
 *   - Optional editable label — poster or designer can rename until verify.
 *   - Optional delete affordance (poster/designer until verify).
 *
 * Consumed by both PunchListSection (hub) and ContractorPunchList
 * (portal) so labeling + captions stay identical everywhere.
 */
import { useState } from 'react';
import { Pencil, Trash2, Loader2, Check, X } from 'lucide-react';
import { thumbUrl, onThumbError } from '../utils/thumbUrl';

const LABEL_MAX = 24;

const fmt = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return ''; }
};

const chipClass = (label) => {
  const l = (label || '').toLowerCase();
  if (l === 'after') return 'bg-green-600 text-white';
  if (l === 'before') return 'bg-slate-800 text-white';
  if (l === 'progress') return 'bg-blue-600 text-white';
  if (l === 'question') return 'bg-amber-500 text-white';
  return 'bg-[#70170F] text-white';
};

export default function PunchPhotoTile({
  photo,
  darkMode = false,
  canDelete = false,
  canEditLabel = false,
  onDelete,
  onSaveLabel,   // async (newLabel) => void
  size = 'md',   // 'sm' (thumb 160px) | 'md' (320) | 'lg' (600)
  testIdBase = 'punch-photo',
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!photo?.url) return null;

  const label = photo.label || photo.type || '';
  const uploader = photo.uploaded_by_name || 'Contributor';
  const when = fmt(photo.uploaded_at);
  const sizePx = size === 'lg' ? 600 : size === 'sm' ? 160 : 320;
  const border = darkMode ? 'border-[#3A3A3A]' : 'border-slate-200';
  const bg = darkMode ? 'bg-[#0D0D0D]' : 'bg-slate-100';
  const captionColor = darkMode ? 'text-slate-400' : 'text-slate-500';

  const beginEdit = (e) => {
    e.stopPropagation();
    setDraft(label);
    setEditing(true);
  };
  const cancelEdit = (e) => {
    e?.stopPropagation();
    setEditing(false);
    setDraft('');
  };
  const commitEdit = async (e) => {
    e?.stopPropagation();
    const next = draft.trim().slice(0, LABEL_MAX);
    if (!next || next === label) { cancelEdit(); return; }
    if (!onSaveLabel) { cancelEdit(); return; }
    setSaving(true);
    try {
      await onSaveLabel(next);
      setEditing(false);
      setDraft('');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (e) => {
    e?.stopPropagation();
    if (!onDelete) return;
    setDeleting(true);
    try {
      await onDelete();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="relative group" data-testid={testIdBase}>
      <a
        href={photo.url}
        target="_blank"
        rel="noreferrer"
        className={`block aspect-square rounded overflow-hidden border ${border} ${bg}`}
        title={`${uploader} · ${when}`}
      >
        <img
          src={thumbUrl(photo, sizePx)}
          alt={label || 'punch photo'}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover"
          onError={onThumbError(photo.url)}
        />
      </a>

      {/* Corner chip — small, upper-left, does NOT overlay text. */}
      {label && !editing && (
        <div
          className={`absolute top-1 left-1 flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide shadow-sm ${chipClass(label)}`}
          data-testid={`${testIdBase}-chip`}
        >
          <span className="truncate max-w-[80px]">{label}</span>
          {canEditLabel && (
            <button
              type="button"
              onClick={beginEdit}
              className="opacity-70 hover:opacity-100"
              aria-label="Edit label"
              data-testid={`${testIdBase}-label-edit`}
            >
              <Pencil className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
      )}

      {/* Editable label input. */}
      {editing && (
        <div
          className={`absolute top-1 left-1 right-1 flex items-center gap-1 rounded px-1.5 py-1 shadow ${darkMode ? 'bg-[#0D0D0D] border border-[#3A3A3A]' : 'bg-white border border-slate-300'}`}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={LABEL_MAX}
            autoFocus
            placeholder="e.g. Existing, Question"
            className={`flex-1 min-w-0 text-[11px] px-1 py-0.5 rounded outline-none ${darkMode ? 'bg-[#0D0D0D] text-slate-100' : 'bg-white text-slate-800'}`}
            data-testid={`${testIdBase}-label-input`}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitEdit(e);
              if (e.key === 'Escape') cancelEdit(e);
            }}
          />
          <button
            type="button"
            onClick={commitEdit}
            disabled={saving}
            className="text-green-600 hover:text-green-700 disabled:opacity-50"
            aria-label="Save label"
            data-testid={`${testIdBase}-label-save`}
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          </button>
          <button
            type="button"
            onClick={cancelEdit}
            disabled={saving}
            className="text-slate-500 hover:text-slate-700"
            aria-label="Cancel"
            data-testid={`${testIdBase}-label-cancel`}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Delete affordance — hover reveal. */}
      {canDelete && (
        <button
          type="button"
          onClick={doDelete}
          disabled={deleting}
          className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 text-white rounded p-1 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
          aria-label="Delete photo"
          data-testid={`${testIdBase}-delete`}
        >
          {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
        </button>
      )}

      {/* Caption BELOW the photo — no more overlay collisions. */}
      <div className={`mt-1 text-[10px] leading-tight ${captionColor}`}>
        <div className="truncate" data-testid={`${testIdBase}-uploader`}>{uploader}</div>
        <div>{when}</div>
      </div>
    </div>
  );
}
